import React, { useState, useMemo } from 'react';
import { motion } from 'framer-motion';
import {
  ChevronRight,
  ChevronLeft,
  Search,
  Filter,
  Trash2,
  User,
  MapPin,
  Package,
  Building2,
  SwitchCamera as UserSwitch,
} from 'lucide-react';
import { usePilgrimGuideStore } from '../../../stores/pilgrimGuideStore';
import { PilgrimGuideRow } from './PilgrimGuideRow';
import { useLanguage } from '../../../contexts/LanguageContext';
import { StatusBadge } from './StatusBadge';
import { TableFilters } from './filters/TableFilters';
import { FilterState } from './types';
import { ConfirmDialog } from '../../common/ConfirmDialog';
import { EvidenceModal } from '../evidence/EvidenceModal';
import { EvidenceViewModal } from '../evidence/EvidenceViewModal';
import { TransferGuideModal } from '../modal/TransferGuideModal';
import toast from 'react-hot-toast';
import { getDoc, doc } from 'firebase/firestore';
import { firestore } from '../../../lib/firebase/config';

const PAGE_SIZE_KEY = 'pilgrim_guide_page_size';
const PAGE_SIZE_OPTIONS = [10, 50, 100];

type PilgrimLink = {
  id: string;
  pilgrimName: string;
  passportNumber: string;
  residenceCountry: string;
  status: string;
  gender: 'male' | 'female';
  packageName: string;
  pilgrimType: string;
  nusukGroup: string;
  guideName: string;
  organizerName: string;
  guideId: string;
  organizerId: string;
  disputeId?: string;
  evidence?: any;
  isOwner?: boolean;
};

type Props = {
  links: PilgrimGuideLink[];
  searchTerm: string;
  onSearchChange: (value: string) => void;
  selectedStatus: string;
  onStatusChange: (value: string) => void;
  selectedCountry: string;
  onCountryChange: (value: string) => void;
};

export const PilgrimGuideTable = ({
  links,
  searchTerm,
  onSearchChange,
  selectedStatus,
  onStatusChange,
  selectedCountry,
  onCountryChange,
}: Props) => {
  const { translations, direction } = useLanguage();
  const { deleteLink } = usePilgrimGuideStore();
  const userRole = localStorage.getItem('userRole');
  const userId = localStorage.getItem('userId');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(() => {
    const savedSize = localStorage.getItem(PAGE_SIZE_KEY);
    return savedSize ? parseInt(savedSize) : 10;
  });
  const [filters, setFilters] = useState<FilterState>({
    search: '',
    status: '',
    country: '',
    ownership: 'all',
  });
  const [deleteConfirm, setDeleteConfirm] = useState<{
    isOpen: boolean;
    link: PilgrimLink | null;
  }>({
    isOpen: false,
    link: null,
  });

  // Add state for evidence and transfer modals
  const [selectedLink, setSelectedLink] = useState<PilgrimLink | null>(null);
  const [isEvidenceModalOpen, setIsEvidenceModalOpen] = useState(false);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [isTransferModalOpen, setIsTransferModalOpen] = useState(false);
  const [selectedOwnership, setSelectedOwnership] = useState('all');

  // Get unique countries for filter
  const countries = useMemo(() => {
    return Array.from(
      new Set(links.map((link) => link.residenceCountry))
    ).sort();
  }, [links]);

  const handleFilterChange = (key: keyof FilterState, value: string) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
    setCurrentPage(1); // Reset to first page when filters change
  };

  // Filter links based on search and filters
  const filteredLinks = useMemo(() => {
    return links.filter((link) => {
      const matchesSearch =
        !searchTerm ||
        link.pilgrimName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        String(link.passportNumber)
          .toLowerCase()
          .includes(searchTerm.toLowerCase());

      const matchesStatus = !selectedStatus || link.status === selectedStatus;
      const matchesCountry =
        !selectedCountry || link.residenceCountry === selectedCountry;

      // Add ownership filter
      const matchesOwnership = 
        selectedOwnership === 'all' ||
        (selectedOwnership === 'owner' && link.isOwner !== false) ||
        (selectedOwnership === 'notOwner' && link.isOwner === false);

      return matchesSearch && matchesStatus && matchesCountry && matchesOwnership;
    });
  }, [links, searchTerm, selectedStatus, selectedCountry, selectedOwnership]);

  // Calculate pagination
  const totalPages = Math.ceil(filteredLinks.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedLinks = filteredLinks.slice(startIndex, startIndex + itemsPerPage);

  const handleItemsPerPageChange = (value: number) => {
    setItemsPerPage(value);
    setCurrentPage(1); // Reset to first page when changing items per page
    localStorage.setItem(PAGE_SIZE_KEY, value.toString());
  };

  const handleDelete = async (link: PilgrimLink) => {
    try {
      await deleteLink(link.id, translations);
      toast.success(
        translations?.PilgrimGuide?.messages?.deleteSuccess ||
          'Link deleted successfully'
      );
    } catch (error) {
      toast.error(
        translations?.PilgrimGuide?.messages?.deleteError ||
          'Failed to delete the link'
      );
    }
    setDeleteConfirm({ isOpen: false, link: null });
  };

  const canManageLink = (link: PilgrimLink) =>
    userRole === 'admin' ||
    (userRole === 'guide' && userId === link.guideId) ||
    (userRole === 'organizer' && userId === link.organizerId);

  const canManageEvidence = (link: PilgrimLink) => {
    if (userRole === 'admin') return true;
    if (userRole === 'guide' && userId === link.guideId) return true;
    if (userRole === 'organizer' && userId === link.organizerId) return true;
    return false;
  };

  // Show transfer button for admin and organizer
  const showTransfer = userRole === 'admin' || userRole === 'organizer';

  const handleEvidenceClick = async (
    link: PilgrimLink,
    action: 'submit' | 'view' | 'edit'
  ) => {
    if (!link.disputeId) {
      toast.error(
        translations?.PilgrimGuide?.errors?.noDispute ||
          'No dispute available to submit evidence'
      );
      return;
    }

    // Fetch latest evidence data from Firestore
    try {
      const disputeDoc = await getDoc(
        doc(firestore, 'Disputes', link.disputeId)
      );
      if (disputeDoc.exists()) {
        const disputeData = disputeDoc.data();
        const guideEvidence = disputeData.disputingGuides?.find(
          (g: any) => g.guideId === link.guideId
        )?.evidence;
        link.evidence = guideEvidence; // Update the link with latest evidence
      }
    } catch (error) {
      console.error('Error fetching evidence:', error);
    }

    setSelectedLink(link);
    if (action === 'view') {
      setIsViewModalOpen(true);
    } else {
      setIsEvidenceModalOpen(true);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white rounded-lg shadow-sm"
    >
      {/* Always show filters */}
      <TableFilters
        searchTerm={searchTerm}
        onSearchChange={onSearchChange}
        selectedStatus={selectedStatus}
        onStatusChange={onStatusChange}
        selectedCountry={selectedCountry}
        onCountryChange={onCountryChange}
        selectedOwnership={selectedOwnership}
        onOwnershipChange={setSelectedOwnership}
        countries={Array.from(
          new Set(links.map((link) => link.residenceCountry))
        )}
        filteredLinks={filteredLinks}
      />

      {/* Only show table if there are filtered results */}
      {filteredLinks.length > 0 && (
        <div className="overflow-x-auto">
          <div className="min-w-full">
            {/* Mobile View */}
            <div className="md:hidden">
              {paginatedLinks.map((link) => (
                <motion.div
                  key={link.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="p-4 border-b last:border-b-0 hover:bg-gray-50"
                >
                  {/* Pilgrim Info */}
                  <div className="flex items-center gap-3 mb-4">
                    <div
                      className={`p-2 rounded-full ${
                        link.gender === 'male'
                          ? 'bg-blue-50'
                          : link.gender === 'female'
                            ? 'bg-pink-50'
                            : 'bg-gray-50'
                      }`}
                    >
                      <User
                        className={
                          link.gender === 'male'
                            ? 'text-blue-500'
                            : link.gender === 'female'
                              ? 'text-pink-500'
                              : 'text-gray-500'
                        }
                        size={20}
                      />
                    </div>
                    <div>
                      <div className="font-medium">{link.pilgrimName}</div>
                      <div className="text-sm text-gray-500">
                        {translations?.pilgrimInfo?.passport || 'Passport'}:{' '}
                        {link.passportNumber}
                      </div>
                      <div className="flex items-center gap-2 mt-1 text-sm text-gray-500">
                        <MapPin size={14} />
                        <span>{link.residenceCountry}</span>
                      </div>
                    </div>
                  </div>

                  {/* Package Info */}
                  <div className="mb-4">
                    <div className="flex items-center gap-2 text-sm">
                      <Package size={16} className="text-primary-500" />
                      <span className="font-medium">{link.packageName}</span>
                    </div>
                    <div className="text-xs text-gray-500 mt-1">
                      {translations?.packageInfo?.pilgrimType || 'Pilgrim Type'}
                      : {link.pilgrimType}
                    </div>
                    <div className="text-xs text-gray-500">
                      {translations?.packageInfo?.nusukGroup || 'Nusuk Group'}:{' '}
                      {link.nusukGroup}
                    </div>
                  </div>

                  {/* Guide Info */}
                  <div className="mb-4">
                    <div className="flex items-center gap-2">
                      <Building2 size={16} className="text-primary-500" />
                      <div className="font-medium">{link.guideName}</div>
                    </div>
                    <div className="text-sm text-gray-500 mt-1">
                      {link.organizerName}
                    </div>
                  </div>

                  {/* Status and Actions */}
                  <div className="flex items-center justify-between mt-4">
                    <StatusBadge
                      status={link.status}
                      evidence={link.evidence}
                      isOwner={link.isOwner}
                      onSubmitEvidence={
                        canManageEvidence(link) &&
                        link.status === 'disputed' &&
                        !link.evidence
                          ? () => handleEvidenceClick(link, 'submit')
                          : undefined
                      }
                      onViewEvidence={
                        link.evidence
                          ? () => handleEvidenceClick(link, 'view')
                          : undefined
                      }
                      onEditEvidence={
                        canManageEvidence(link) &&
                        link.status === 'disputed' &&
                        link.evidence
                          ? () => handleEvidenceClick(link, 'edit')
                          : undefined
                      }
                    />
                    <div className="flex items-center gap-2">
                      {/* Add Transfer Button for Mobile */}
                      {showTransfer && link.status !== 'waiting_identification' && (
                        <button
                          onClick={() => {
                            setSelectedLink(link);
                            setIsTransferModalOpen(true);
                          }}
                          className="p-2 text-primary-500 hover:bg-primary-50 rounded-lg transition-colors"
                          title={translations?.actions?.transfer || 'Transfer'}
                        >
                          <UserSwitch size={18} />
                        </button>
                      )}
                      {canManageLink(link) && (
                        <button
                          onClick={() => setDeleteConfirm({ isOpen: true, link })}
                          className="p-2 text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                          title={translations?.actions?.delete || 'Delete'}
                        >
                          <Trash2 size={18} />
                        </button>
                      )}
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>

            {/* Desktop View */}
            <table className="hidden md:table min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th
                    className={`px-6 py-3 text-sm font-medium text-gray-500 ${
                      direction === 'ltr' ? 'text-left' : 'text-right'
                    }`}
                  >
                    {translations?.pilgrimGuideTable?.headers?.pilgrimInfo ||
                      'Pilgrim Info'}
                  </th>
                  <th
                    className={`px-6 py-3 text-sm font-medium text-gray-500 ${
                      direction === 'ltr' ? 'text-left' : 'text-right'
                    }`}
                  >
                    {translations?.pilgrimGuideTable?.headers?.package ||
                      'Package'}
                  </th>
                  <th
                    className={`px-6 py-3 text-sm font-medium text-gray-500 ${
                      direction === 'ltr' ? 'text-left' : 'text-right'
                    }`}
                  >
                    {translations?.pilgrimGuideTable?.headers
                      ?.guideAndOrganizer || 'Guide & Organizer'}
                  </th>
                  <th
                    className={`px-6 py-3 text-sm font-medium text-gray-500 ${
                      direction === 'ltr' ? 'text-left' : 'text-right'
                    }`}
                  >
                    {translations?.pilgrimGuideTable?.headers?.status ||
                      'Status'}
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {paginatedLinks.map((link) => (
                  <PilgrimGuideRow key={link.id} link={link} />
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Pagination */}
      {filteredLinks.length > 0 && (
        <div className="bg-white px-4 py-3 flex items-center justify-between border-t">
          <div className="flex items-center gap-4">
            {/* Items per page selector */}
            <select
              value={itemsPerPage}
              onChange={(e) => handleItemsPerPageChange(parseInt(e.target.value))}
              className="px-2 py-1 border rounded-lg text-sm focus:ring-2 focus:ring-primary-500"
            >
              {PAGE_SIZE_OPTIONS.map(size => (
                <option key={size} value={size}>
                  {size} {translations?.pagination?.perPage || 'per page'}
                </option>
              ))}
            </select>

            {/* Pagination controls */}
            <div className="flex items-center gap-2">
              <button
                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                disabled={currentPage === 1}
                className={`p-1 rounded-lg ${
                  currentPage === 1
                    ? 'text-gray-400 cursor-not-allowed'
                    : 'text-gray-600 hover:bg-gray-100'
                }`}
              >
                {direction === 'ltr' ? (
                  <ChevronLeft size={20} />
                ) : (
                  <ChevronRight size={20} />
                )}
              </button>

              <span className="text-sm text-gray-700">
                {translations?.pilgrimGuideTable?.pagination?.page || 'Page'}{' '}
                {currentPage}{' '}
                {translations?.pilgrimGuideTable?.pagination?.of || 'of'}{' '}
                {totalPages}
              </span>

              <button
                onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages}
                className={`p-1 rounded-lg ${
                  currentPage === totalPages
                    ? 'text-gray-400 cursor-not-allowed'
                    : 'text-gray-600 hover:bg-gray-100'
                }`}
              >
                {direction === 'ltr' ? (
                  <ChevronRight size={20} />
                ) : (
                  <ChevronLeft size={20} />
                )}
              </button>
            </div>
          </div>

          <div className="text-sm text-gray-500">
            {translations?.pagination?.showing || 'Showing'} {startIndex + 1}-{Math.min(startIndex + itemsPerPage, filteredLinks.length)} {translations?.pagination?.of || 'of'} {filteredLinks.length}
          </div>
        </div>
      )}

      {/* Modals */}
      {selectedLink && (
        <>
          <EvidenceModal
            isOpen={isEvidenceModalOpen}
            onClose={() => {
              setIsEvidenceModalOpen(false);
              setSelectedLink(null);
            }}
            disputeId={selectedLink.disputeId!}
            guideId={selectedLink.guideId}
          />

          <EvidenceViewModal
            isOpen={isViewModalOpen}
            onClose={() => {
              setIsViewModalOpen(false);
              setSelectedLink(null);
            }}
            guideName={selectedLink.guideName}
            evidence={selectedLink.evidence}
          />

          <TransferGuideModal
            isOpen={isTransferModalOpen}
            onClose={() => {
              setIsTransferModalOpen(false);
              setSelectedLink(null);
            }}
            linkId={selectedLink.id}
            currentGuideId={selectedLink.guideId}
            pilgrimId={selectedLink.id}
            pilgrimName={selectedLink.pilgrimName}
          />
        </>
      )}

      {/* Confirm Dialog */}
      <ConfirmDialog
        isOpen={deleteConfirm.isOpen}
        onClose={() => setDeleteConfirm({ isOpen: false, link: null })}
        onConfirm={() => handleDelete(deleteConfirm.link!)}
        title={
          translations?.PilgrimGuide?.confirmDialog?.deleteTitle ||
          'Delete Link'
        }
        message={
          deleteConfirm.link?.status === 'disputed'
            ? translations?.PilgrimGuide?.confirmDialog?.disputeMessage?.replace(
                '{name}',
                deleteConfirm.link?.pilgrimName || ''
              )
            : translations?.PilgrimGuide?.confirmDialog?.defaultMessage?.replace(
                '{name}',
                deleteConfirm.link?.pilgrimName || ''
              )
        }
        type="danger"
        confirmText={
          translations?.PilgrimGuide?.confirmDialog?.delete || 'Delete'
        }
        cancelText={
          translations?.PilgrimGuide?.confirmDialog?.cancel || 'Cancel'
        }
      />
    </motion.div>
  );
};
